import { Maximize2, Minimize2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "zustand";
import { uiStore } from "@/lib/stores/ui-store";

const MIN_W = 300;
const MIN_H = 200;
const MINIMIZED_W = 160;
const MINIMIZED_H = 40;
const MAX_W_RATIO = 0.5;
const MAX_H_RATIO = 0.5;
const EDGE_PADDING = 12;
const HANDLE_SIZE = 6;

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

type SpotlightOverlayProps = {
	children: ReactNode;
	header?: ReactNode;
	label?: string;
};

export function SpotlightOverlay({
	children,
	header,
	label = "Overlay",
}: SpotlightOverlayProps) {
	const overlayPos = useStore(
		uiStore,
		(state) => state.spotlightOverlayPosition
	);
	const setOverlayPos = useStore(
		uiStore,
		(state) => state.setSpotlightOverlayPosition
	);

	const containerRef = useRef<HTMLDivElement>(null);
	const [minimized, setMinimized] = useState(false);

	const [offset, setOffset] = useState({ x: 0, y: 0 });
	const [resizeOffset, setResizeOffset] = useState({
		x: 0,
		y: 0,
		w: 0,
		h: 0,
	});
	const isDragging = useRef(false);
	const isResizing = useRef(false);
	const dragStart = useRef({ mx: 0, my: 0 });
	const resizeStart = useRef({ mx: 0, my: 0 });
	const resizeDir = useRef<ResizeDirection>("se");

	const getResolvedPos = useCallback(() => {
		const container = containerRef.current;
		if (!container) return overlayPos;
		const rect = container.getBoundingClientRect();
		if (overlayPos.x === -1 || overlayPos.y === -1) {
			return {
				x: rect.width - overlayPos.w - EDGE_PADDING,
				y: rect.height - overlayPos.h - EDGE_PADDING,
				w: overlayPos.w,
				h: overlayPos.h,
			};
		}
		return overlayPos;
	}, [overlayPos]);

	const clamp = useCallback(
		(
			val: { x: number; y: number; w: number; h: number },
			options: { skipMinSize?: boolean } = {}
		) => {
			const container = containerRef.current;
			if (!container) return val;
			const bounds = container.getBoundingClientRect();
			const maxW = bounds.width * MAX_W_RATIO;
			const maxH = bounds.height * MAX_H_RATIO;
			const cw = options.skipMinSize
				? Math.min(val.w, maxW)
				: Math.max(MIN_W, Math.min(val.w, maxW));
			const ch = options.skipMinSize
				? Math.min(val.h, maxH)
				: Math.max(MIN_H, Math.min(val.h, maxH));
			const cx = Math.max(0, Math.min(val.x, bounds.width - cw));
			const cy = Math.max(0, Math.min(val.y, bounds.height - ch));
			return { x: cx, y: cy, w: cw, h: ch };
		},
		[]
	);

	const computeResizeDelta = useCallback(
		(dir: ResizeDirection, dx: number, dy: number) => {
			let rx = 0;
			let ry = 0;
			let rw = 0;
			let rh = 0;
			if (dir.includes("e")) rw = dx;
			if (dir.includes("w")) {
				rw = -dx;
				rx = dx;
			}
			if (dir.includes("s")) rh = dy;
			if (dir.includes("n")) {
				rh = -dy;
				ry = dy;
			}
			return { x: rx, y: ry, w: rw, h: rh };
		},
		[]
	);

	const onDragPointerDown = useCallback((e: React.PointerEvent) => {
		if ((e.target as HTMLElement).closest("[data-resize-handle]")) return;
		if ((e.target as HTMLElement).closest("[data-minimize-btn]")) return;
		e.preventDefault();
		isDragging.current = true;
		dragStart.current = { mx: e.clientX, my: e.clientY };
		setOffset({ x: 0, y: 0 });
		(e.target as HTMLElement).setPointerCapture(e.pointerId);
	}, []);

	const onDragPointerMove = useCallback((e: React.PointerEvent) => {
		if (!isDragging.current) return;
		setOffset({
			x: e.clientX - dragStart.current.mx,
			y: e.clientY - dragStart.current.my,
		});
	}, []);

	const onDragPointerUp = useCallback(
		(e: React.PointerEvent) => {
			if (!isDragging.current) return;
			isDragging.current = false;
			const resolved = getResolvedPos();
			const dx = e.clientX - dragStart.current.mx;
			const dy = e.clientY - dragStart.current.my;
			const visualW = minimized ? MINIMIZED_W : resolved.w;
			const visualH = minimized ? MINIMIZED_H : resolved.h;
			const clamped = clamp(
				{ x: resolved.x + dx, y: resolved.y + dy, w: visualW, h: visualH },
				{ skipMinSize: minimized }
			);
			setOverlayPos({ ...clamped, w: resolved.w, h: resolved.h });
			setOffset({ x: 0, y: 0 });
		},
		[getResolvedPos, clamp, setOverlayPos, minimized]
	);

	const onResizePointerDown = useCallback(
		(dir: ResizeDirection) => (e: React.PointerEvent) => {
			e.preventDefault();
			e.stopPropagation();
			isResizing.current = true;
			resizeDir.current = dir;
			resizeStart.current = { mx: e.clientX, my: e.clientY };
			setResizeOffset({ x: 0, y: 0, w: 0, h: 0 });
			(e.target as HTMLElement).setPointerCapture(e.pointerId);
		},
		[]
	);

	const onResizePointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (!isResizing.current) return;
			const dx = e.clientX - resizeStart.current.mx;
			const dy = e.clientY - resizeStart.current.my;
			setResizeOffset(computeResizeDelta(resizeDir.current, dx, dy));
		},
		[computeResizeDelta]
	);

	const onResizePointerUp = useCallback(
		(e: React.PointerEvent) => {
			if (!isResizing.current) return;
			isResizing.current = false;
			const pos = getResolvedPos();
			const dx = e.clientX - resizeStart.current.mx;
			const dy = e.clientY - resizeStart.current.my;
			const delta = computeResizeDelta(resizeDir.current, dx, dy);
			const clamped = clamp({
				x: pos.x + delta.x,
				y: pos.y + delta.y,
				w: pos.w + delta.w,
				h: pos.h + delta.h,
			});
			setOverlayPos(clamped);
			setResizeOffset({ x: 0, y: 0, w: 0, h: 0 });
		},
		[getResolvedPos, clamp, setOverlayPos, computeResizeDelta]
	);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const rect = container.getBoundingClientRect();
		if (overlayPos.x === -1 || overlayPos.y === -1) {
			setOverlayPos({
				x: rect.width - overlayPos.w - EDGE_PADDING,
				y: rect.height - overlayPos.h - EDGE_PADDING,
			});
		} else if (!minimized) {
			const clamped = clamp(overlayPos);
			if (
				clamped.x !== overlayPos.x ||
				clamped.y !== overlayPos.y ||
				clamped.w !== overlayPos.w ||
				clamped.h !== overlayPos.h
			) {
				setOverlayPos(clamped);
			}
		}
	}, [
		overlayPos.x,
		overlayPos.y,
		overlayPos.w,
		overlayPos.h,
		setOverlayPos,
		clamp,
		minimized,
	]);

	const handleRestore = useCallback(() => {
		const p = getResolvedPos();
		const clamped = clamp(p);
		setOverlayPos(clamped);
		setMinimized(false);
	}, [getResolvedPos, clamp, setOverlayPos]);

	const pos = getResolvedPos();
	const displayX = pos.x + offset.x + resizeOffset.x;
	const displayY = pos.y + offset.y + resizeOffset.y;
	const displayW = pos.w + resizeOffset.w;
	const displayH = pos.h + resizeOffset.h;

	const cornerSize = HANDLE_SIZE * 2;

	const handles: {
		dir: ResizeDirection;
		cursor: string;
		style: React.CSSProperties;
	}[] = [
		// Edges
		{
			dir: "n",
			cursor: "ns-resize",
			style: {
				top: 0,
				left: cornerSize,
				right: cornerSize,
				height: HANDLE_SIZE,
			},
		},
		{
			dir: "s",
			cursor: "ns-resize",
			style: {
				bottom: 0,
				left: cornerSize,
				right: cornerSize,
				height: HANDLE_SIZE,
			},
		},
		{
			dir: "w",
			cursor: "ew-resize",
			style: {
				left: 0,
				top: cornerSize,
				bottom: cornerSize,
				width: HANDLE_SIZE,
			},
		},
		{
			dir: "e",
			cursor: "ew-resize",
			style: {
				right: 0,
				top: cornerSize,
				bottom: cornerSize,
				width: HANDLE_SIZE,
			},
		},
		// Corners
		{
			dir: "nw",
			cursor: "nwse-resize",
			style: { top: 0, left: 0, width: cornerSize, height: cornerSize },
		},
		{
			dir: "ne",
			cursor: "nesw-resize",
			style: { top: 0, right: 0, width: cornerSize, height: cornerSize },
		},
		{
			dir: "sw",
			cursor: "nesw-resize",
			style: { bottom: 0, left: 0, width: cornerSize, height: cornerSize },
		},
		{
			dir: "se",
			cursor: "nwse-resize",
			style: { bottom: 0, right: 0, width: cornerSize, height: cornerSize },
		},
	];

	return (
		<div className="pointer-events-none absolute inset-0" ref={containerRef}>
			<div
				className="pointer-events-auto absolute z-10 cursor-grab overflow-visible rounded-lg border bg-background shadow-lg active:cursor-grabbing"
				onPointerDown={onDragPointerDown}
				onPointerMove={onDragPointerMove}
				onPointerUp={onDragPointerUp}
				style={{
					left: `${displayX}px`,
					top: `${displayY}px`,
					width: minimized ? `${MINIMIZED_W}px` : `${displayW}px`,
					height: minimized ? `${MINIMIZED_H}px` : `${displayH}px`,
					transition: minimized ? "width 0.2s, height 0.2s" : undefined,
				}}
			>
				{minimized ? (
					<div className="flex h-full w-full items-center justify-between rounded-lg bg-card px-3">
						<span className="text-muted-foreground text-xs">{label}</span>
						<button
							className="flex size-6 items-center justify-center rounded hover:bg-accent"
							data-minimize-btn
							onClick={handleRestore}
							title="Restore overlay"
							type="button"
						>
							<Maximize2 className="size-3 text-muted-foreground" />
						</button>
					</div>
				) : (
					<>
						<div className="flex h-full w-full flex-col overflow-hidden rounded-lg">
							{header && (
								<div className="flex shrink-0 items-center gap-2 border-b bg-card px-3 py-1.5">
									<div className="min-w-0 flex-1">{header}</div>
									<button
										className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
										data-minimize-btn
										onClick={() => setMinimized(true)}
										title="Minimize overlay"
										type="button"
									>
										<Minimize2 className="size-4" />
									</button>
								</div>
							)}
							<div className="flex-1 overflow-hidden">{children}</div>
						</div>
						{!header && (
							<button
								className="absolute top-1 right-1 z-30 flex size-6 items-center justify-center rounded bg-background/80 backdrop-blur-sm hover:bg-accent"
								data-minimize-btn
								onClick={() => setMinimized(true)}
								title="Minimize overlay"
								type="button"
							>
								<Minimize2 className="size-3 text-muted-foreground" />
							</button>
						)}
						{handles.map((handle) => (
							<div
								className="absolute z-20"
								data-resize-handle
								key={handle.dir}
								onPointerDown={onResizePointerDown(handle.dir)}
								onPointerMove={onResizePointerMove}
								onPointerUp={onResizePointerUp}
								style={{ ...handle.style, cursor: handle.cursor }}
							/>
						))}
					</>
				)}
			</div>
		</div>
	);
}
